import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { subscriptionAPI, authAPI } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export default function BillingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [packageName, setPackageName] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await subscriptionAPI.getCheckoutStatus(sessionId);
      
      if (response.data.status === 'paid') {
        setStatus('success');
        setPackageName(response.data.package_id);
        
        // Refresh user data
        const userResponse = await authAPI.getMe();
        // Update context if needed
      } else {
        setStatus('checking');
        // Retry after a delay
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      setStatus('failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-12">
        {status === 'checking' && (
          <Card className="p-12 text-center rounded-2xl">
            <Loader2 className="mx-auto mb-4 animate-spin text-primary" size={48} />
            <h2 className="text-2xl font-serif mb-2">Processing Your Payment</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your subscription...
            </p>
          </Card>
        )}

        {status === 'success' && (
          <Card className="p-12 text-center rounded-2xl" data-testid="payment-success">
            <CheckCircle2 className="mx-auto mb-4 text-accent" size={64} />
            <h2 className="text-3xl font-serif mb-4">Welcome to Premium! 🎉</h2>
            <p className="text-muted-foreground mb-8">
              Your subscription is now active. Enjoy unlimited access to all features!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                className="rounded-full bg-primary"
                data-testid="goto-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/coach')}
                variant="outline"
                className="rounded-full"
              >
                Start Chatting with Clair
              </Button>
            </div>
          </Card>
        )}

        {status === 'failed' && (
          <Card className="p-12 text-center rounded-2xl" data-testid="payment-failed">
            <XCircle className="mx-auto mb-4 text-destructive" size={64} />
            <h2 className="text-2xl font-serif mb-4">Payment Failed</h2>
            <p className="text-muted-foreground mb-8">
              We couldn't process your payment. Please try again or contact support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/pricing')}
                className="rounded-full bg-primary"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="rounded-full"
              >
                Back to Dashboard
              </Button>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
